import { UploadIcon, XIcon } from "lucide-react";
import React, { useEffect, useEffectEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	FileUpload,
	FileUploadDropzone,
	FileUploadItem,
	FileUploadItemDelete,
	FileUploadItemMetadata,
	FileUploadItemPreview,
	FileUploadList,
	type FileUploadRootProps,
	FileUploadTrigger,
	ITEM_PREVIEW_NAME,
	useFileUploadContext,
	useFileUploadItemContext,
} from "@/components/ui/file-upload";
import type { SafeOmit } from "@/lib/utils";
import { Lightbox } from "../lightbox";

type Value = string | File | undefined;

type Multiple = {
	multiple: true;
	value: Array<Value>;
	onChange: (files: Array<Value>) => void;
};
type Single = { multiple: false; value: Value; onChange: (files: Value) => void };

type InputFileProps = SafeOmit<FileUploadRootProps, "value" | "multiple"> & (Multiple | Single);

const URLStringToFile = async (url: string, signal?: AbortSignal) => {
	try {
		const response = await fetch(url, { signal });
		const blob = await response.blob();
		const fileName = url.split("/").pop()?.split("?")[0] || "image.jpg";
		return new File([blob], fileName, { type: blob.type });
	} catch (error) {
		if (error instanceof DOMException && error.name === "AbortError") {
			return null;
		}
		throw error;
	}
};

const MAX_SIZE = 5_242_880; // 5mb
const MAX_FILENAME_LENGTH = 20;

export function InputFile({ multiple, value, onChange, ...props }: InputFileProps) {
	const [files, setFiles] = React.useState<Array<File>>([]);
	const maxFileSize = props.maxSize || MAX_SIZE;
	const { t } = useTranslation();

	const onFileReject = React.useCallback(
		(file: File, message: string) => {
			toast(message, {
				description: `"${
					file.name.length > MAX_FILENAME_LENGTH ? `${file.name.slice(0, MAX_FILENAME_LENGTH)}...` : file.name
				}" ${t(($) => $.common.components.inputFile.hasBeenRejected)}`,
			});
		},
		[t]
	);

	const loadFiles = useEffectEvent(async (signal: AbortSignal) => {
		const v = multiple ? value : [value || undefined];

		const filesData = v.map((file) => {
			if (typeof file === "string") {
				return URLStringToFile(file, signal);
			}
			return Promise.resolve(file);
		});

		const results = await Promise.allSettled(filesData);

		if (signal.aborted) return;

		const resolvedFiles = results
			.filter(
				(result): result is PromiseFulfilledResult<File> => result.status === "fulfilled" && result.value != null
			)
			.map((result) => result.value);

		setFiles(resolvedFiles);
	});

	useEffect(() => {
		const controller = new AbortController();

		loadFiles(controller.signal);

		return () => controller.abort();
	}, []);

	const onValueChange = React.useCallback(
		(newFiles: Array<File>) => {
			setFiles(newFiles);

			if (multiple) {
				onChange(newFiles);
			} else {
				onChange(newFiles[0]);
			}
		},
		[multiple, onChange]
	);

	return (
		<FileUpload
			accept="image/*"
			maxFiles={props.maxFiles || 1}
			multiple={multiple}
			onFileReject={onFileReject}
			onValueChange={onValueChange}
			value={files}
			{...props}
			maxSize={maxFileSize}
		>
			<FileUploadDropzone>
				<div className="flex flex-col items-center gap-1">
					<div className="flex items-center justify-center rounded-full border p-2.5">
						<UploadIcon className="size-6 text-muted-foreground" />
					</div>
					<p className="font-medium text-sm">{t(($) => $.common.components.inputFile.title)}</p>
					<p className="text-muted-foreground text-xs">
						{t(($) => $.common.components.inputFile.Description, {
							maxFiles: props.maxFiles ? props.maxFiles.toString() : "1",
						})}
					</p>
				</div>
				<FileUploadTrigger asChild>
					<Button className="mt-2 w-fit" size="sm" variant="outline">
						{t(($) => $.common.components.inputFile.browseFiles)}
					</Button>
				</FileUploadTrigger>
			</FileUploadDropzone>

			<FileUploadList>
				{files.map((file) => (
					<FileUploadItem key={file.name} value={file}>
						<FileUploadItemPreviewLightbox>
							<FileUploadItemPreview />
						</FileUploadItemPreviewLightbox>
						<FileUploadItemMetadata className="overflow-hidden text-clip" />
						<FileUploadItemDelete asChild>
							<Button className="size-7" size="icon" variant="ghost">
								<XIcon />
							</Button>
						</FileUploadItemDelete>
					</FileUploadItem>
				))}
			</FileUploadList>
		</FileUpload>
	);
}

function FileUploadItemPreviewLightbox(props: { children: React.ReactNode }) {
	const itemContext = useFileUploadItemContext(ITEM_PREVIEW_NAME);
	const context = useFileUploadContext(ITEM_PREVIEW_NAME);
	const { t } = useTranslation();

	const previewLabel = t(($) => $.common.components.inputFile.filePreview);

	const url = React.useMemo(() => {
		if (itemContext.fileState?.file) {
			return context.urlCache.get(itemContext.fileState.file);
		}
		return null;
	}, [context.urlCache, itemContext.fileState]);

	const altText = React.useMemo(() => {
		if (itemContext.fileState?.file) {
			return itemContext.fileState.file.name || previewLabel;
		}
		return previewLabel;
	}, [itemContext.fileState, previewLabel]);

	if (!url) {
		return <>{props.children}</>;
	}

	return (
		<Lightbox alt={altText} title={altText} url={url}>
			{props.children}
		</Lightbox>
	);
}
